import type { MenuServiceBadge as MenuServiceBadgeData } from '@ordo/constants/menu-service-badge'
/**
 * /orders Personal variant data layer (school picker + active menus).
 *
 * Two reads:
 *   1. household (reused from `api/family.ts`) — drives the school picker
 *   2. `useLocationActiveMenusQuery(locationId, activeOn)` — menus +
 *      sections + items for the selected school + date.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { type MyFamilyResponse, useMyFamilyQuery } from './family'
import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Re-export the family hook + types for the school-picker fetch path.
// ---------------------------------------------------------------------------

export type MyHouseholdSchoolsResponse = MyFamilyResponse
export const useMyHouseholdSchoolsQuery = useMyFamilyQuery

// ---------------------------------------------------------------------------
// LocationActiveMenusQuery — REST replacement
// ---------------------------------------------------------------------------

/**
 * Where the queried date sits relative to the location's menu history — only
 * populated when the query passes `activeOn`. Drives the family day view's
 * differentiated "no menu" empty states.
 */
export interface MenusDayContext {
  /** The queried service day, `YYYY-MM-DD`. */
  date: string
  firstMenuStartDate: string | null
  lastMenuEndDate: string | null
  /** Start of the earliest menu after the date that has orderable items. */
  nextMenuStartDate: string | null
  prevMenuEndDate: string | null
  /** Set when the term calendar marks the date NO_SERVICE. */
  noServiceDay: { notes: string | null } | null
}

/**
 * A HOLIDAY TREAT served at the campus on a date + mealtime — free with every
 * meal ordered, never orderable itself. Rendered as a gift row on the day
 * view / order sheet; `menuItemId` opens the item detail modal.
 */
export interface HolidayTreatWire {
  /** Service day, `YYYY-MM-DD`. */
  date: string
  mealtime: string
  menuId: string
  menuItemId: string
  componentId: string
  name: string
  description: string | null
  imageUri: string | null
  /** Treats per meal (min 1). */
  perMeal: number
  allergens: Array<{ id: string; name: string }>
  dietaryTags: string[]
  allergenCoverage: string
}

export interface LocationActiveMenusResponse {
  /** Populated (possibly null) whenever the query passed `activeOn`. */
  dayContext?: MenusDayContext | null
  /**
   * Holiday treats served on the queried day (empty when none, or when the
   * query passed no `activeOn`). Optional so cached pre-treat payloads parse.
   */
  holidayTreats?: HolidayTreatWire[]
  menusForLocation: Array<{
    id: string
    name: string
    /**
     * Customer-facing section title ("Mains", "Sides, Drinks & Desserts"),
     * defaulted at menu creation and editable in the menus app. Ordering
     * surfaces render `displayName ?? name`; null on historical rows.
     * Optional so cached pre-`displayName` payloads keep parsing.
     */
    displayName?: string | null
    mealtime: 'BREAKFAST' | 'LUNCH' | 'SNACK' | 'SUPPER'
    /**
     * Per-actor audience marker stamped by the browse projection. ENTITLED =
     * restricted-diet menu, ELECTED = a field trip meal the school opened to
     * this household for the date; both carry `entitledStudentUserIds` so the
     * client gates per child. Absent/CUSTOMER = campus-wide.
     */
    serviceBadge?: MenuServiceBadgeData | null
    audienceTier?: 'CUSTOMER' | 'ENTITLED' | 'ELECTED'
    entitledStudentUserIds?: string[]
    /**
     * Non-field-trip menus only: household students the school's field-trip-only
     * election closes this menu to for the date. The client hides the menu for
     * exactly those children.
     */
    blockedStudentUserIds?: string[]
    description: string | null
    menuLine: {
      code: string
      name: string
    } | null
    startDate: string
    endDate: string
    timeZone: string
    gradeRangeCodes: string[]
    /** Org-wide display position — menus render as sections in this order. */
    sortOrder: number
    isArchived: boolean
    /** The menu spans `activeOn` but that date is in its blockedDates. */
    blockedOnDate?: boolean
    /**
     * The menu spans `activeOn` but that WEEKDAY is switched off on its service
     * days (`MenuServiceLine.serviceDays`) — the school doesn't serve that day
     * of the week. The server has already filtered every item out, so without
     * this flag an unserved weekday is indistinguishable from an unfinished
     * menu. Optional so cached pre-flag payloads keep parsing.
     */
    outsideServiceDays?: boolean
    sections: Array<{
      id: string
      name: string
      icon: string | null
      sortOrder: number
      isArchived: boolean
    }>
    items: Array<{
      id: string
      sectionId: string | null
      itemKind: 'BUNDLE' | 'COMPONENT'
      name: string | null
      sortOrder: number
      visible: boolean
      isArchived: boolean
      customerPriceCents: number | null
      /**
       * FACTORY-643 — what an adult diner (not a student at the campus)
       * pays instead; null = the same as students. Optional: cached
       * payloads from before the field lack it.
       */
      adultPriceCents?: number | null
      currency: string | null
      /**
       * ZERO-source guard (pricing rework Phase E): false when the
       * pricing resolver matched no tier — the item is UNPRICED.
       * Money-carrying personas (parents) render "Price not set" and
       * disable add-to-cart. A deliberate free item resolves with
       * `isPriced: true` + `customerPriceCents: 0`.
       */
      isPriced: boolean
      /**
       * Allergens for the item, populated only when the query opts in via
       * `withAllergens`. `null` = not requested or unknown (e.g. a bundle);
       * `[]` = requested and the item carries no reviewed allergen tags
       * (i.e. allergen-free).
       */
      allergens?: Array<{ id: string; name: string }> | null
      /**
       * Dietary tag display names (e.g. "Vegetarian"), populated alongside
       * `allergens` when the query opts in via `withAllergens`. Reviewed
       * non-HAZARD rollup tags — visible on the item and filterable, per the
       * allergen-vs-dietary split (allergens stay on nutrition surfaces).
       */
      dietaryTags?: string[] | null
      /**
       * Contains-rollup evidence coverage, populated alongside `allergens`
       * when the query opts in via `withAllergens`. 'UNKNOWN' = the item has
       * NO Contains evidence at all (typical for custom items) — an empty
       * `allergens` list must not read as verified allergen-free. Detail
       * surfaces render an "Allergens unknown" state; grid surfaces ignore it.
       */
      allergenCoverage?: 'EVIDENCED' | 'UNKNOWN' | null
      /**
       * Bagged-lunch (field-trip) marker. Only ever true on the school
       * ordering surface — parents never receive field-trip items.
       */
      serviceBadge?: MenuServiceBadgeData | null
      isFieldTrip?: boolean
      /**
       * Canonical milk-option code when this row is the menu's
       * system-materialized standalone milk (menu-milk-materializer).
       * `null`/absent = ordinary item, including hand-placed à-la-carte
       * milk. The school order sheet groups these rows under "Milk —
       * included with meals" and derives their default quantities from
       * meal counts × the BEVERAGE delivery rate × the milk shares.
       */
      lineupMilkOptionCode?: string | null
      /**
       * School-wide daily order cap for this item (bagged lunches). Drives the
       * "Max N per day" chip and the school-order-submit cap gate. `null` =
       * uncapped.
       */
      quantityCap?: number | null
      /**
       * Column hard-gate (field-trip scope). Non-empty = the item is only
       * orderable for these LocationGradeRange columns (age group) or
       * `(dimension, value)` group scopes; the grid renders a dash
       * elsewhere.
       */
      allowedLocationGradeRangeIds: string[]
      allowedGroupScopes: Array<{ dimension: string; value: string }> | null
      /**
       * Meal-column provenance: the template bucket ("Daily Special",
       * "Veg Special", …) this item composes into. Set for template-derived
       * items and for instance-added extras the school authored into a
       * bucket's column — ordering surfaces group the latter under the meal
       * instead of "À la carte". Optional so cached pre-rollout payloads stay
       * renderable.
       */
      mealColumn?: { bucketId: string; bucketCode: string; bucketLabel: string } | null
      bundle: {
        id: string
        name: string
        displayName: string | null
        imageUri: string | null
        description: string | null
        /**
         * Addendum G — single sealed container: percentages can't apply
         * inside, so the OVS order sheet shows "Ships complete" and no
         * Meal-parts breakdown.
         */
        shipsComplete: boolean
        /** One entry per resolved pick (kind + whether its deliverable is bulk). */
        parts: Array<{ componentKind: string; isBulk: boolean }>
        /**
         * CONDIMENT components shipping with each meal (name + per-meal
         * quantity) — read-only display rows on the school order sheet.
         * Optional so cached pre-rollout payloads stay renderable.
         */
        condiments?: Array<{ name: string; quantity: number }>
      } | null
      component: {
        id: string
        name: string
        /** Family/student-facing label — customer surfaces show this over `name` when present. */
        displayName: string | null
        description: string | null
        imageUri: string | null
        kind: string
      } | null
      /**
       * A meal's parts AS SERVED on the browsed date, present only when a
       * dated substitution rule swapped or omitted one of them (PROD-1386).
       * The publication snapshot's component list is frozen at publish time
       * and cannot know; when this is set it wins over the snapshot. Absent
       * or null = served as published.
       */
      servedComponents?: Array<{
        id: string
        name: string
        description: string | null
        imageUri: string | null
        componentKind: string | null
      }> | null
    }>
  }>
}

export interface MenuWeekPreviewItem {
  menuItemId: string
  name: string
  imageUri: string | null
}

/** One service day's "On the menu" teaser for the family week grid. */
export interface MenuWeekPreviewDay {
  /** Service day, `YYYY-MM-DD`. */
  date: string
  /** Distinct items on the day's menus — may exceed `items.length`. */
  totalCount: number
  items: MenuWeekPreviewItem[]
  /**
   * The school isn't serving this day at all — a blocked date, a weekday
   * switched off on its service days, or a day outside the school session.
   * Distinct from an empty teaser, which can also mean "menu not authored
   * yet". Optional so cached pre-flag payloads keep parsing.
   */
  noService?: boolean
  /** The free holiday treat served that day, when there is one. */
  holidayTreat?: { name: string; imageUri: string | null; mealtime: string } | null
}

export interface MenuWeekPreviewResponse {
  days: MenuWeekPreviewDay[]
}

export interface NextOpenMenuWeekResponse {
  nextOpenWeek: {
    date: string
    weekStart: string
    locationId: string
    locationName: string
    menuId: string
    menuName: string
    mealtime: 'BREAKFAST' | 'LUNCH' | 'SNACK' | 'SUPPER'
  } | null
}

export async function fetchLocationActiveMenus(
  args: {
    locationId: string
    activeOn?: string | null
    mealtime?: string | null
    withAllergens?: boolean
    fieldTripPreview?: boolean
  },
  signal?: AbortSignal,
): Promise<LocationActiveMenusResponse> {
  const { menus, dayContext, holidayTreats } = await apiFetch<{
    menus: LocationActiveMenusResponse['menusForLocation']
    dayContext?: MenusDayContext | null
    holidayTreats?: HolidayTreatWire[]
  }>('/api/menus', {
    query: {
      locationId: args.locationId,
      activeOn: args.activeOn ?? undefined,
      mealtime: args.mealtime ?? undefined,
      withAllergens: args.withAllergens ? '1' : undefined,
      fieldTripPreview: args.fieldTripPreview ? 'true' : undefined,
    },
    signal,
  })
  // Mirror the previous GraphQL response shape — pages use the
  // `menusForLocation` key.
  return { menusForLocation: menus, dayContext: dayContext ?? null, holidayTreats: holidayTreats ?? [] }
}

/** Holiday treats served at a campus across a date range (`YYYY-MM-DD`, inclusive). */
export async function fetchHolidayTreats(
  args: { locationId: string; from: string; to: string },
  signal?: AbortSignal,
): Promise<HolidayTreatWire[]> {
  const { treats } = await apiFetch<{ treats: HolidayTreatWire[] }>('/api/menus/holiday-treats', {
    query: { locationId: args.locationId, from: args.from, to: args.to },
    signal,
  })
  return treats
}

export async function fetchNextOpenMenuWeek(
  args: { locationIds: string[]; asOf?: string | null; mealtime?: string | null; gradeRangeCode?: string | null },
  signal?: AbortSignal,
): Promise<NextOpenMenuWeekResponse> {
  return apiFetch<NextOpenMenuWeekResponse>('/api/menus/next-open-week', {
    query: {
      locationIds: args.locationIds.join(','),
      asOf: args.asOf ?? undefined,
      mealtime: args.mealtime ?? undefined,
      gradeRangeCode: args.gradeRangeCode ?? undefined,
    },
    signal,
  })
}

/**
 * Shared key builder — the family-day route loader prefetches with the SAME
 * key the hook reads, so keep both on this function.
 */
export const locationActiveMenusQueryKey = (args: {
  locationId: string
  activeOn?: string | null
  mealtime?: string | null
  withAllergens?: boolean
}) => ['menus', args.locationId, args.activeOn ?? null, args.mealtime ?? null, args.withAllergens ?? false] as const

/** Published-menu staleness: menus effectively never change intra-session for a browsing parent. */
export const MENUS_STALE_TIME_MS = 5 * 60_000

export function useLocationActiveMenusQuery(args: {
  locationId: string
  activeOn?: string | null
  mealtime?: string | null
  withAllergens?: boolean
}) {
  return useQuery<LocationActiveMenusResponse>({
    queryKey: locationActiveMenusQueryKey(args),
    queryFn: ({ signal }) => fetchLocationActiveMenus(args, signal),
    // Deliberate deviation from the 30s list default (AGENTS.md): published
    // menus don't change intra-day for a browsing parent, and this endpoint
    // is the most expensive read in the app — don't refetch it per date
    // click. keepPreviousData keeps the day list rendered while `activeOn`
    // changes (same-shape navigation), so date arrows don't flash skeletons.
    staleTime: MENUS_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    enabled: Boolean(args.locationId),
  })
}

export async function fetchMenuWeekPreview(
  args: { locationId: string; weekStart: string; gradeRangeCode?: string | null },
  signal?: AbortSignal,
): Promise<MenuWeekPreviewResponse> {
  return apiFetch<MenuWeekPreviewResponse>('/api/menus/week-preview', {
    query: {
      locationId: args.locationId,
      weekStart: args.weekStart,
      ...(args.gradeRangeCode ? { gradeRangeCode: args.gradeRangeCode } : {}),
    },
    signal,
  })
}

/**
 * "On the menu" teasers for each service day of one location's week.
 * `gradeRangeCode` scopes the teaser to one band (omit for unfiltered —
 * mixed-band households). Callers pass `enabled: false` for a band-less
 * student (fail-closed: they see no menus, so skip the fetch).
 */
export function useMenuWeekPreviewQuery(args: {
  locationId: string
  weekStart: string
  gradeRangeCode?: string | null
  enabled?: boolean
}) {
  return useQuery<MenuWeekPreviewResponse>(menuWeekPreviewQueryOptions(args))
}

/**
 * The same query as an options object, for callers that fan out over several
 * campuses with `useQueries` (a day card can't call the hook in a loop). Same
 * key, so a card and a section share one cached fetch per (location, week).
 */
export function menuWeekPreviewQueryOptions(args: {
  locationId: string
  weekStart: string
  gradeRangeCode?: string | null
  enabled?: boolean
}) {
  return {
    queryKey: ['menus', 'week-preview', args.locationId, args.weekStart, args.gradeRangeCode ?? null] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchMenuWeekPreview(args, signal),
    // Same rationale as useLocationActiveMenusQuery: published-menu teasers
    // are stable intra-session; week arrows shouldn't refetch or flash.
    staleTime: MENUS_STALE_TIME_MS,
    placeholderData: keepPreviousData<MenuWeekPreviewResponse>,
    enabled: args.enabled !== false && Boolean(args.locationId && args.weekStart),
  }
}

export function useNextOpenMenuWeekQuery(args: {
  locationIds: string[]
  asOf?: string | null
  mealtime?: string | null
  gradeRangeCode?: string | null
  enabled?: boolean
}) {
  const locationKey = args.locationIds.slice().sort().join(',')
  return useQuery<NextOpenMenuWeekResponse>({
    queryKey: [
      'menus',
      'next-open-week',
      locationKey,
      args.asOf ?? null,
      args.mealtime ?? null,
      args.gradeRangeCode ?? null,
    ],
    queryFn: ({ signal }) => fetchNextOpenMenuWeek(args, signal),
    enabled: args.enabled !== false && args.locationIds.length > 0,
  })
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Collapse a household's memberships into the distinct list of school
 * Locations the parent's kids are enrolled at. Filters out
 * archived memberships + locations.
 */
export function distinctSchoolLocations(
  data: MyHouseholdSchoolsResponse | null | undefined,
): Array<{ id: string; name: string; timeZone: string }> {
  const out = new Map<string, { id: string; name: string; timeZone: string }>()
  const households = (data?.me?.ownedAccounts ?? []).filter((a) => a.kind === 'HOUSEHOLD' && !a.isArchived)
  for (const member of households.flatMap((household) => household.members)) {
    if (member.isArchived || !member.user) {
      continue
    }
    for (const ms of member.user.memberships) {
      if (ms.isArchived || !ms.location) {
        continue
      }
      if (ms.location.type !== 'SCHOOL_CAMPUS') {
        continue
      }
      out.set(ms.location.id, {
        id: ms.location.id,
        name: ms.location.name,
        timeZone: ms.location.timeZone,
      })
    }
  }
  return Array.from(out.values()).sort((a, b) => a.name.localeCompare(b.name))
}
